'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceAreaCardProps {
  area: {
    id: number;
    name: string;
    slug: string;
    image: string;
    alt: string;
    description: string;
    neighborhoods: string[];
    specialties: string[];
    responseTime: string;
    testimonialCount: number;
  };
}

export default function ServiceAreaCard({ area }: ServiceAreaCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-card rounded-lg shadow-brand overflow-hidden hover:shadow-brand-elevated transition-brand-complex">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={area.image}
          alt={area.alt}
          className="w-full h-full object-cover hover:scale-105 transition-brand-complex"
        />
        <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-body font-medium shadow-cta-glow">
          {area.responseTime}
        </div>
      </div>

      <div className="p-6">
        <h3 className="font-headline font-bold text-xl text-primary mb-2">
          {area.name}
        </h3>
        
        <p className="text-muted-foreground font-body text-sm mb-4">
          {area.description}
        </p>

        <div className="space-y-3 mb-4">
          <div className="flex items-start space-x-2">
            <Icon name="MapPinIcon" size={20} className="text-secondary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-body font-medium text-foreground mb-1">
                Neighborhoods Served:
              </p>
              <p className="text-xs text-muted-foreground font-body">
                {area.neighborhoods.slice(0, isExpanded ? undefined : 3).join(', ')}
                {!isExpanded && area.neighborhoods.length > 3 && '...'}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-2">
            <Icon name="SparklesIcon" size={20} className="text-secondary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-body font-medium text-foreground mb-1">
                Local Specialties:
              </p>
              <div className="flex flex-wrap gap-1">
                {area.specialties.map((specialty, index) => (
                  <span
                    key={index}
                    className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded font-body"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Icon name="StarIcon" size={20} className="text-warning" variant="solid" />
            <p className="text-xs text-muted-foreground font-body">
              {area.testimonialCount} verified local testimonials
            </p>
          </div>
        </div>

        {area.neighborhoods.length > 3 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-primary hover:text-secondary font-body font-medium mb-4 flex items-center space-x-1 transition-brand"
          >
            <span>{isExpanded ? 'Show Less' : 'Show All Neighborhoods'}</span>
            <Icon
              name="ChevronDownIcon"
              size={16}
              className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}

        <div className="flex space-x-3">
          <Link
            href={`/service-areas/${area.slug}`}
            className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-body font-medium text-center hover:bg-primary/90 transition-brand"
          >
            View Details
          </Link>
          <Link
            href="/request-quote"
            className="flex-1 bg-accent text-accent-foreground px-4 py-2 rounded-md text-sm font-body font-medium text-center hover:bg-accent/90 shadow-cta-glow transition-brand"
          >
            Get Quote
          </Link>
        </div>
      </div>
    </div>
  );
}